import React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, MessageSquare, BookOpen } from 'lucide-react';


const FAQ  = ({ faqModalOpen, setFaqModalOpen, setContactModalOpen }) => { 
    const FaqItem = ({ question, answer }) => {
        const [isOpen, setIsOpen] = useState(false);
      
        return (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <button
              className="w-full px-4 py-3 text-left flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              onClick={() => setIsOpen(!isOpen)}
              aria-expanded={isOpen}
            >
              <span className="font-medium text-gray-900 dark:text-white">{question}</span>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className={`h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                viewBox="0 0 20 20" 
                fill="currentColor"
              >
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            {isOpen && (
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300">{answer}</p>
              </div>
            )}
          </div>
        );
      };

  return (  
    <>  
    {faqModalOpen && (
        <div 
            className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={() => setFaqModalOpen(false)}
        >
            <div 
            className="bg-white dark:bg-gray-900 rounded-xl max-w-6xl w-full p-8 shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
            >
            <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-200 dark:border-gray-700">
                <div>
                <h3 className="text-2xl font-bold dark:text-white flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Frequently Asked Questions
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Can't find what you're looking for? <button onClick={() => { setContactModalOpen(true); setFaqModalOpen(false); }} className="text-blue-600 dark:text-blue-400 hover:underline">Contact our team</button>
                </p>
                </div>
                <button
                onClick={() => setFaqModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Close FAQ"
                >
                <X size={24} />
                </button>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
                    General Questions
                    </h4>
                    <div className="space-y-4">
                    <FaqItem 
                        question="What does 0x10LayerFF do?"
                        answer="0x10LayerFF provides comprehensive cybersecurity solutions with 16 integrated protection layers (0x10 in hexadecimal). Our platform offers threat prevention, detection, response, and recovery services for businesses of all sizes."
                    />
                    <FaqItem 
                        question="How is 0x10LayerFF different from other security providers?"
                        answer="Unlike point solutions, we offer a unified security platform with AI-driven threat intelligence, automated remediation, and compliance management—all accessible through a single dashboard with 24/7 monitoring."
                    />
                    <FaqItem 
                        question="Do you offer free trials?"
                        answer="Yes, we provide a 14-day free trial of our enterprise platform with full functionality. No credit card required. Contact our sales team to discuss extended evaluation periods for larger deployments."
                    />
                    </div>
                </div>

                <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
                    Technical Requirements
                    </h4>
                    <div className="space-y-4">
                    <FaqItem 
                        question="What systems do you support?"
                        answer="Our platform supports Windows, macOS, Linux, and cloud environments (AWS, Azure, GCP). We provide agents for endpoints, APIs for cloud integration, and network appliances for on-premises deployment."
                    />
                    <FaqItem 
                        question="How do you handle data privacy?"
                        answer="All customer data is encrypted in transit (TLS 1.3) and at rest (AES-256). We never access your sensitive data without explicit permission and comply with GDPR, CCPA, and other privacy regulations."
                    />
                    <FaqItem 
                        question="What's your uptime SLA?"
                        answer="We guarantee 99.9% uptime for our cloud services, with 24/7 monitoring and redundant systems across multiple availability zones. Detailed SLA terms are available in our enterprise contracts."
                    />
                    </div>
                </div>
                </div>

                <div className="space-y-6">
                <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
                    Pricing & Plans
                    </h4>
                    <div className="space-y-4">
                    <FaqItem 
                        question="What pricing models do you offer?"
                        answer="We provide flexible pricing: per-user/month for SMBs, annual enterprise licensing, and custom quotes for large deployments. All plans include 24/7 support and regular threat intelligence updates."
                    />
                    <FaqItem 
                        question="Are there setup fees?"
                        answer="Standard deployment has no setup fees. Complex enterprise implementations may incur professional services fees, which we outline upfront during the sales process."
                    />
                    <FaqItem 
                        question="Do you offer nonprofit discounts?"
                        answer="Yes, registered 501(c)(3) organizations receive 25% off standard pricing. Educational institutions may qualify for additional discounts through our EDU program."
                    />
                    </div>
                </div>

                <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
                    Security Operations
                    </h4>
                    <div className="space-y-4">
                    <FaqItem 
                        question="How quickly do you respond to threats?"
                        answer="Our SOC team responds to critical alerts within 15 minutes, 24/7/365. Automated containment begins immediately for known threat patterns, with human verification for novel threats."
                    />
                    <FaqItem 
                        question="How do you handle false positives?"
                        answer="Our AI systems continuously learn from analyst feedback to reduce false positives. You can customize sensitivity thresholds and create allow lists for business-critical processes."
                    />
                    <FaqItem 
                        question="Do you provide compliance reporting?"
                        answer="Yes, our platform generates pre-built reports for SOC 2, ISO 27001, HIPAA, and PCI DSS. Custom report templates can be created for specific regulatory requirements."
                    />
                    </div>
                </div>
                </div>
            </div>

            {/*<div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Still have questions?</h4>
                <div className="flex flex-col sm:flex-row gap-4">
                <button
                    onClick={() => setContactModalOpen(true)}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium flex items-center justify-center gap-2"
                >
                    <MessageSquare size={16} />
                    Chat with Support
                </button>
                <a
                    href="/documentation"
                    className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium flex items-center justify-center gap-2"
                >
                    <BookOpen size={16} />
                    View Documentation
                </a>
                </div>
            </div>*/}
            </div>
        </div>
        )}
    </>
  );
};

export default FAQ;